import { useEffect, useRef } from "react";
import { Carousel } from "@/components/ui/motion-framer/apple-cards-carousel";
import { SliderNavButtons } from "@/components/public/SliderNavButtons";

const appleCards = [
  {
    quote: "Exceptional service from Replenico. I have been with them for over 3 years and they have catered for my business effectively! Definitely recommend.",
    customer: "Ayodeji Odiri, GB",
  },
  {
    quote: "Great, reliable service and a great partner for my apartments.",
    customer: "Tony A, GB",
  },
  {
    quote: "The service and team at Replenico are exceptional, providing great customer service. We have been working with them for a number of years, and their service has been invaluable to property managers like us.",
    customer: "Gus Taylor, GB",
  },
  {
    quote: "Great service and team - have a number of units with Replenico and highly recommended the service.",
    customer: "Sophia, GB",
  },
  {
    quote: "Very impressed, extremely well organised and really helpful. Bedding is good quality but what I'm most happy with is how easy they make it for me.",
    customer: "Jos Jos, GB",
  },
  {
    quote: "Great personalised service! Great customer service tailored to fit my needs! One stop shop for everything I need to run my short lets.",
    customer: "Nikhil Bhatt, GB",
  },
  {
    quote: "Replenico have been a real lifesaver in terms of saving us both time and money. The team has been incredibly supportive and responsive throughout, making the whole process smooth and easy.",
    customer: "Henry Sava, GB",
  },
  {
    quote: "Fantastic service from Replenico Manchester. They responded so quickly to my queries, delivered on time, beautiful linen, collected on request. Excellent service thank you.",
    customer: "Angela, GB",
  },
  // New reviews below
  {
    quote: "5 star service - makes running short term lets a breeze! Cleaning quality is excellent and linen hire service makes hosting short term guests a breeze.",
    customer: "Rachel Moore, GB",
  },
  {
    quote: "Forward-thinking business, great service. Can always rely on Replenico for a turnaround, friendly team too!",
    customer: "Henry Popiolek, FR",
  },
  {
    quote: "Highly recommended, great service. We're very happy with the customer service of their ops team, drivers and accounts team.",
    customer: "Morca Relocations Limited, US",
  },
  {
    quote: "The best cleaning service in town. Always reliable. Always helpful. Always professional. Always clean!",
    customer: "Boutique D&M, GB",
  },
];

// Repeat the cards array to simulate endless loop
const loopedCards = [...appleCards, ...appleCards, ...appleCards];
const CARD_WIDTH = 360; // fallback width
const CARD_GAP = 16;
// Start from the left instead of center
const initialScroll = 0;

export function AppleCardsSection() {
  const cardRef = useRef<HTMLDivElement>(null);

  // Scroll handler for arrows
  const handleArrowScroll = (direction: "left" | "right") => {
    const carousel = document.querySelector('.flex.w-full.overflow-x-scroll');
    let cardWidth = CARD_WIDTH;
    if (cardRef.current instanceof HTMLDivElement) {
      cardWidth = cardRef.current.offsetWidth;
    }
    if (carousel) {
      (carousel as HTMLElement).scrollBy({
        left: direction === "right" ? cardWidth + CARD_GAP : -(cardWidth + CARD_GAP),
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="w-full bg-[var(--homepage-primary)] py-16 sm:py-20 xl:py-24 z-10 relative">
      <div className="w-full">
        {/* Header Section - Contained with max-w-7xl */}
        <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%] mb-12 sm:mb-16">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="flex-1">
              {/* Eyebrow */}
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-3">
                Testimonials
              </span>
              
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-white leading-tight mb-4">
                What Our Clients Say
              </h2>
              
              <p className="text-base sm:text-lg font-light text-white/80 max-w-2xl leading-relaxed">
                Don&apos;t just take our word for it. See how Replenico is transforming property management for businesses across the UK.
              </p>
            </div>

            {/* Navigation Arrows - Next to Header */}
            <SliderNavButtons
              onPrev={() => handleArrowScroll("left")}
              onNext={() => handleArrowScroll("right")}
              darkMode={true}
              className="sm:ml-8"
            />
          </div>
        </div>

        {/* Carousel Container - Full Width */}
        <div className="w-full relative">
          {/* Left fade overlay */}
          <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-16 xl:w-24 z-10 pointer-events-none bg-gradient-to-r from-[var(--homepage-primary)] to-transparent" />
          
          {/* Right fade overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-16 xl:w-24 z-10 pointer-events-none bg-gradient-to-l from-[var(--homepage-primary)] to-transparent" />
          
          <Carousel
          initialScroll={initialScroll}
          items={loopedCards.map((card, idx) => (
            <div
              key={card.quote + idx}
              ref={idx === 0 ? cardRef : undefined}
              className="rounded-2xl overflow-hidden min-h-[20rem] sm:min-h-[22rem] flex flex-col shadow-lg hover:shadow-xl transition-all duration-300 mx-auto max-w-[85vw] sm:max-w-[22rem] xl:max-w-[26rem] bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10"
            >
              {/* Colored top bar with gradient */}
              <div 
                className="h-3 w-full"
                style={{ 
                  background: 'linear-gradient(135deg, var(--homepage-accent, #14b8a6) 0%, #0891b2 100%)'
                }}
              />
              
              {/* Card Content */}
              <div className="flex-1 flex flex-col justify-between p-6 sm:p-7 xl:p-8">
                {/* Top: Quote */}
                <div>
                  {/* Large quote mark */}
                  <div className="text-[2.5rem] sm:text-[3rem] text-teal-400 font-serif leading-none mb-3 opacity-50">
                    &#8220;
                  </div>
                  
                  {/* Quote text */}
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-1">
                    {card.quote}
                  </p>
                  
                  {/* Closing quote mark inline */}
                  <span className="text-2xl sm:text-3xl text-teal-400 font-serif opacity-50">
                    &#8221;
                  </span>
                </div>

                {/* Bottom: Rating and customer */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  {/* Star rating */}
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" />
                      </svg>
                    ))}
                  </div>
                  
                  {/* Customer name */}
                  <p className="text-white/60 text-xs sm:text-sm font-medium">
                    {card.customer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        />
        </div>
      </div>
    </section>
  );
}
